import type { StructureDef, ZoneDef } from '@jianghu/game-data';
import { createRng } from '@jianghu/shared';
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { part, shade } from '../entities/geometry';
import { plaqueTexture, roofTileTexture } from './textures';

/** Geometry buckets merged into a handful of draw calls for the whole zone. */
interface Buckets {
  std: THREE.BufferGeometry[];
  roof: THREE.BufferGeometry[];
  glow: THREE.BufferGeometry[];
}

export interface AnimatedProp {
  update(t: number, dt: number): void;
}

export interface StructureBuild {
  group: THREE.Group;
  animated: AnimatedProp[];
  /** Interaction anchors for shrines (waypoint crystals). */
  shrineCrystals: THREE.Object3D[];
  campfires: THREE.Vector3[];
}

const WOOD = '#4b2f22';
const WOOD_LIGHT = '#7a5332';
const STONE = '#8f8b80';
const STONE_DARK = '#6d6a62';
const RED = '#9b2c2c';
const LANTERN = '#ff5a3a';

const LABELS_ZH: Record<string, string> = {
  'Elder’s Hall': '长老堂',
  'Lin’s Apothecary': '林氏药铺',
  'Zhou’s Forge': '周家铁铺',
  'Eastern Gate': '柳溪村',
  'Northern Gate': '柳溪村',
  'Southern Gate': '柳溪村',
};

function placeMatrix(s: { x: number; z: number; rot: number }, y = 0): THREE.Matrix4 {
  return new THREE.Matrix4().makeRotationY(s.rot).setPosition(s.x, y, s.z);
}

function push(list: THREE.BufferGeometry[], g: THREE.BufferGeometry, m: THREE.Matrix4): void {
  g.applyMatrix4(m);
  list.push(g);
}

/**
 * Curved Chinese gable roof with upturned eaves. Ridge runs along local X at height `rise`;
 * eaves at y=0, z=±(d/2+overhang). Includes UVs for the tile texture and vertex colours for tint.
 */
export function roofGeometry(
  w: number,
  d: number,
  rise: number,
  overhang: number,
  lift: number,
  color: string,
): THREE.BufferGeometry {
  const nu = 14;
  const nv = 8;
  const halfW = w / 2 + overhang;
  const halfD = d / 2 + overhang;
  const pos: number[] = [];
  const uv: number[] = [];
  const col: number[] = [];
  const c = new THREE.Color(color);
  const point = (u: number, v: number, side: number): [number, number, number] => {
    const x = u * halfW;
    const z = side * v * halfD;
    const y =
      rise * Math.pow(1 - v, 1.5) +
      lift * Math.pow(Math.abs(u), 4) * v * v +
      0.12 * rise * Math.pow(Math.abs(u), 3) * (1 - v);
    return [x, y, z];
  };
  for (const side of [1, -1]) {
    for (let i = 0; i < nu; i++) {
      for (let j = 0; j < nv; j++) {
        const u0 = -1 + (2 * i) / nu;
        const u1 = -1 + (2 * (i + 1)) / nu;
        const v0 = j / nv;
        const v1 = (j + 1) / nv;
        const quad: [number, number][] = [
          [u0, v0],
          [u1, v0],
          [u1, v1],
          [u0, v1],
        ];
        const tri = side === 1 ? [0, 3, 2, 0, 2, 1] : [0, 1, 2, 0, 2, 3];
        for (const k of tri) {
          const [u, v] = quad[k] as [number, number];
          pos.push(...point(u, v, side));
          uv.push((u * halfW) / 0.45, (v * halfD) / 0.34);
          const shadeK = 0.86 + 0.14 * (1 - v);
          col.push(c.r * shadeK, c.g * shadeK, c.b * shadeK);
        }
      }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.computeVertexNormals();
  return g;
}

/** Wall-coloured gable infill under the curved roof (both ends). */
function gableInfill(
  w: number,
  d: number,
  rise: number,
  overhang: number,
  color: string,
): THREE.BufferGeometry[] {
  const halfD = d / 2 + overhang;
  const out: THREE.BufferGeometry[] = [];
  for (const sx of [-1, 1]) {
    const shape = new THREE.Shape();
    const n = 10;
    shape.moveTo(-d / 2, 0);
    for (let i = 0; i <= n; i++) {
      const z = -d / 2 + (d * i) / n;
      const y = rise * Math.pow(1 - Math.abs(z) / halfD, 1.5) - 0.05;
      shape.lineTo(z, Math.max(0, y));
    }
    shape.lineTo(d / 2, 0);
    const g = part.extrude(shape, 0.12, color, {
      pos: [sx * (w / 2 - 0.06), 0, 0],
      rot: [0, Math.PI / 2, 0],
    });
    out.push(g);
  }
  return out;
}

function roofSet(
  b: Buckets,
  m: THREE.Matrix4,
  w: number,
  d: number,
  rise: number,
  overhang: number,
  lift: number,
  roofColor: string,
  wallColor: string,
  y: number,
): void {
  const local = new THREE.Matrix4().makeTranslation(0, y, 0).premultiply(m);
  push(b.roof, roofGeometry(w, d, rise, overhang, lift, roofColor), local);
  for (const g of gableInfill(w, d, rise, overhang, wallColor)) push(b.std, g, local);
  const halfW = w / 2 + overhang;
  // Ridge beam with curled end ornaments.
  push(
    b.std,
    part.box(halfW * 2 * 0.94, 0.22, 0.26, shade(roofColor, -0.08), { pos: [0, rise + 0.08, 0] }),
    local,
  );
  for (const sx of [-1, 1]) {
    push(
      b.std,
      part.cone(
        0.13,
        0.55,
        shade(roofColor, -0.05),
        { pos: [sx * halfW * 0.94, rise + 0.32, 0], rot: [0, 0, -sx * 0.5] },
        6,
      ),
      local,
    );
  }
}

function lantern(b: Buckets, m: THREE.Matrix4, x: number, y: number, z: number): void {
  push(b.std, part.cyl(0.008, 0.008, 0.35, '#2a1d16', { pos: [x, y + 0.35, z] }, 4), m);
  push(b.glow, part.sphere(0.18, LANTERN, { pos: [x, y, z], scale: [1, 1.25, 1] }, 10, 8), m);
  push(b.std, part.cyl(0.1, 0.1, 0.04, '#2a1d16', { pos: [x, y + 0.24, z] }, 8), m);
  push(b.std, part.cyl(0.1, 0.1, 0.04, '#2a1d16', { pos: [x, y - 0.24, z] }, 8), m);
}

function windowGrid(
  b: Buckets,
  m: THREE.Matrix4,
  x: number,
  y: number,
  z: number,
  w: number,
  h: number,
  rotY = 0,
): void {
  const g = new THREE.Group();
  g.rotation.y = rotY;
  g.position.set(x, y, z);
  g.updateMatrix();
  const lm = g.matrix.clone().premultiply(m);
  push(b.std, part.box(w, h, 0.04, '#2a1d16', { pos: [0, 0, 0] }), lm);
  push(b.std, part.box(w - 0.12, h - 0.12, 0.05, '#e9dcc0', { pos: [0, 0, 0.005] }), lm);
  for (let i = 1; i < 4; i++)
    push(b.std, part.box(0.035, h - 0.1, 0.07, WOOD, { pos: [-w / 2 + (w * i) / 4, 0, 0.01] }), lm);
  for (let i = 1; i < 3; i++)
    push(b.std, part.box(w - 0.1, 0.035, 0.07, WOOD, { pos: [0, -h / 2 + (h * i) / 3, 0.01] }), lm);
}

function buildHouse(s: StructureDef, b: Buckets, plaques: THREE.Object3D[], isHall: boolean): void {
  const m = placeMatrix(s);
  const H = s.h ?? 4.2;
  const base = 0.35;
  const wallH = isHall ? H * 0.52 : H * 0.6;
  const rise = H - wallH;
  const wall = s.wallColor ?? '#e9e0cc';
  const roof = s.roofColor ?? '#3b3b44';
  const { w, d } = s;
  push(b.std, part.box(w + 1.2, base, d + 1.2, STONE, { pos: [0, base / 2, 0] }), m);
  push(b.std, part.box(2.2, base * 0.5, 0.8, STONE_DARK, { pos: [0, base * 0.25, d / 2 + 0.95] }), m);
  push(b.std, part.box(w, wallH, d, wall, { pos: [0, base + wallH / 2, 0] }), m);
  push(b.std, part.box(w + 0.04, 0.55, d + 0.04, '#8a8579', { pos: [0, base + 0.275, 0] }), m);
  // Timber frame.
  const postX = [-w / 2, w / 2];
  for (const px of postX) {
    for (const pz of [-d / 2, d / 2]) {
      push(b.std, part.box(0.2, wallH, 0.2, WOOD, { pos: [px, base + wallH / 2, pz] }), m);
    }
  }
  const nMid = Math.max(1, Math.round(w / 3.2) - 1);
  for (let i = 1; i <= nMid; i++) {
    const px = -w / 2 + (w * i) / (nMid + 1);
    push(b.std, part.box(0.16, wallH, 0.16, WOOD, { pos: [px, base + wallH / 2, d / 2 + 0.02] }), m);
    push(b.std, part.box(0.16, wallH, 0.16, WOOD, { pos: [px, base + wallH / 2, -d / 2 - 0.02] }), m);
  }
  for (const pz of [-d / 2 - 0.03, d / 2 + 0.03])
    push(b.std, part.box(w + 0.1, 0.22, 0.16, WOOD, { pos: [0, base + wallH - 0.11, pz] }), m);
  push(b.std, part.box(w + 0.1, 0.12, 0.12, WOOD, { pos: [0, base + 0.62, d / 2 + 0.03] }), m);
  // Door and windows.
  push(b.std, part.box(1.5, 2.25, 0.1, WOOD, { pos: [0, base + 1.125, d / 2 + 0.03] }), m);
  push(b.std, part.box(1.25, 2.1, 0.12, RED, { pos: [0, base + 1.05, d / 2 + 0.04] }), m);
  push(b.std, part.box(0.04, 2.0, 0.14, '#5e1d1d', { pos: [0, base + 1.05, d / 2 + 0.05] }), m);
  for (const sx of [-1, 1]) {
    push(b.std, part.sphere(0.04, '#d4a54a', { pos: [sx * 0.1, base + 1.05, d / 2 + 0.12] }, 6, 4), m);
    windowGrid(b, m, sx * Math.min(w / 2 - 1.1, 2.3), base + 1.45, d / 2 + 0.06, 1.2, 1.0);
    windowGrid(b, m, sx * (w / 2 + 0.06), base + 1.45, 0, 1.0, 0.9, (sx * Math.PI) / 2);
  }
  if (isHall) {
    // Front veranda with red columns and a lower roof sweep.
    const porchZ = d / 2 + 1.25;
    push(b.std, part.box(w + 1.2, base, 2.2, STONE, { pos: [0, base / 2, d / 2 + 1.1] }), m);
    const cols = Math.round(w / 2.6);
    for (let i = 0; i <= cols; i++) {
      const px = -w / 2 + (w * i) / cols;
      push(b.std, part.cyl(0.17, 0.19, wallH + 0.1, RED, { pos: [px, base + wallH / 2, porchZ] }, 10), m);
      push(b.std, part.cyl(0.26, 0.26, 0.2, STONE_DARK, { pos: [px, base + 0.1, porchZ] }, 10), m);
    }
    push(b.std, part.box(w + 0.4, 0.28, 0.24, RED, { pos: [0, base + wallH - 0.05, porchZ] }), m);
    roofSet(b, m, w + 0.6, d + 2.6, rise * 0.55, 0.9, 0.55, roof, wall, base + wallH);
    // Upper tier.
    const upH = rise * 0.5;
    push(
      b.std,
      part.box(w * 0.62, upH, d * 0.55, wall, { pos: [0, base + wallH + rise * 0.42 + upH / 2, 0] }),
      m,
    );
    roofSet(b, m, w * 0.62, d * 0.55, rise * 0.6, 0.8, 0.45, roof, wall, base + wallH + rise * 0.42 + upH);
    for (const sx of [-1, 1]) lantern(b, m, sx * (w / 2 - 0.4), base + wallH - 0.55, porchZ + 0.2);
  } else {
    roofSet(b, m, w, d, rise, 0.85, 0.42, roof, wall, base + wallH);
    for (const sx of [-1, 1]) lantern(b, m, sx * (w / 2 - 0.2), base + wallH - 0.5, d / 2 + 0.75);
  }
  if (s.label && LABELS_ZH[s.label]) {
    const tex = plaqueTexture(LABELS_ZH[s.label] as string);
    const mat = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.6 });
    const plaque = new THREE.Mesh(new THREE.PlaneGeometry(1.9, 0.72), mat);
    const local = new THREE.Vector3(
      0,
      base + wallH - (isHall ? 0.6 : 0.5),
      isHall ? d / 2 + 1.42 : d / 2 + 0.16,
    );
    local.applyMatrix4(m);
    plaque.position.copy(local);
    plaque.rotation.y = s.rot;
    plaques.push(plaque);
  }
}

function buildGate(s: StructureDef, b: Buckets, plaques: THREE.Object3D[]): void {
  const m = placeMatrix(s);
  const H = s.h ?? 6;
  for (const sx of [-1, 1]) {
    const x = (sx * s.w) / 2;
    push(b.std, part.cyl(0.26, 0.3, H, RED, { pos: [x, H / 2, 0] }, 12), m);
    push(b.std, part.box(0.9, 0.7, 0.9, STONE, { pos: [x, 0.35, 0] }), m);
    push(b.std, part.box(0.5, 1.0, 1.1, STONE_DARK, { pos: [x, 0.5, 0] }), m);
  }
  push(b.std, part.box(s.w + 1.6, 0.4, 0.42, RED, { pos: [0, H - 0.6, 0] }), m);
  push(b.std, part.box(s.w + 0.4, 0.3, 0.36, RED, { pos: [0, H - 1.7, 0] }), m);
  push(b.std, part.box(s.w * 0.45, 0.85, 0.28, '#2a1f1a', { pos: [0, H - 1.15, 0] }), m);
  roofSet(b, m, s.w + 1.2, 1.2, 0.9, 0.7, 0.35, '#2e3d4f', RED, H - 0.4);
  if (s.label && LABELS_ZH[s.label]) {
    const tex = plaqueTexture(LABELS_ZH[s.label] as string, '#1f2a3a');
    const mat = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.55, side: THREE.DoubleSide });
    const plaque = new THREE.Mesh(new THREE.PlaneGeometry(s.w * 0.42, 0.75), mat);
    const local = new THREE.Vector3(0, H - 1.15, 0.16).applyMatrix4(m);
    plaque.position.copy(local);
    plaque.rotation.y = s.rot;
    plaques.push(plaque);
    const back = plaque.clone();
    back.position.copy(new THREE.Vector3(0, H - 1.15, -0.16).applyMatrix4(m));
    back.rotation.y = s.rot + Math.PI;
    plaques.push(back);
  }
}

export function buildStructures(zone: ZoneDef): StructureBuild {
  const b: Buckets = { std: [], roof: [], glow: [] };
  const group = new THREE.Group();
  group.name = 'structures';
  const plaques: THREE.Object3D[] = [];
  const animated: AnimatedProp[] = [];
  const shrineCrystals: THREE.Object3D[] = [];
  const campfires: THREE.Vector3[] = [];
  const rng = createRng(zone.seed + 99);

  for (const s of zone.structures) {
    const m = placeMatrix(s);
    switch (s.kind) {
      case 'house':
        buildHouse(s, b, plaques, false);
        break;
      case 'hall':
        buildHouse(s, b, plaques, true);
        break;
      case 'gate':
        buildGate(s, b, plaques);
        break;
      case 'well':
        push(
          b.std,
          part.lathe(
            [
              [1.1, 0.75],
              [1.2, 0.7],
              [1.2, 0],
              [1.0, 0],
              [0.95, 0.7],
            ],
            STONE,
            {},
            16,
          ),
          m,
        );
        push(b.std, part.cyl(0.95, 0.95, 0.05, '#1e3236', { pos: [0, 0.45, 0] }, 16), m);
        for (const sx of [-1, 1])
          push(b.std, part.box(0.14, 2.1, 0.14, WOOD, { pos: [sx * 1.0, 1.05, 0] }), m);
        push(
          b.std,
          part.cyl(0.07, 0.07, 2.2, WOOD_LIGHT, { pos: [0, 1.8, 0], rot: [0, 0, Math.PI / 2] }, 8),
          m,
        );
        roofSet(b, m, 2.0, 1.4, 0.55, 0.35, 0.2, '#3b3b44', '#3b3b44', 2.05);
        push(b.std, part.cyl(0.16, 0.13, 0.25, WOOD_LIGHT, { pos: [0.3, 1.1, 0] }, 8), m);
        push(b.std, part.cyl(0.006, 0.006, 0.6, '#2a1d16', { pos: [0.3, 1.5, 0] }, 4), m);
        break;
      case 'stall': {
        for (const sx of [-1, 1])
          for (const sz of [-1, 1])
            push(b.std, part.box(0.1, 2.2, 0.1, WOOD, { pos: [(sx * s.w) / 2, 1.1, (sz * s.d) / 2] }), m);
        push(b.std, part.box(s.w, 0.1, s.d * 0.8, WOOD_LIGHT, { pos: [0, 0.85, 0.05] }), m);
        push(b.std, part.box(s.w * 0.95, 0.75, 0.05, WOOD, { pos: [0, 0.42, s.d * 0.4] }), m);
        const cloth = s.roofColor ?? '#9a3b2e';
        const canopy = new THREE.PlaneGeometry(s.w + 0.5, s.d + 0.6, 6, 4);
        const p = canopy.getAttribute('position');
        for (let i = 0; i < p.count; i++)
          p.setZ(i, -Math.cos((p.getX(i) / (s.w + 0.5)) * Math.PI) * 0.12 + 0.12);
        canopy.computeVertexNormals();
        const canopyGeo = colorize(canopy, cloth);
        canopyGeo.applyMatrix4(
          new THREE.Matrix4().makeRotationX(-Math.PI / 2 + 0.15).setPosition(0, 2.25, 0),
        );
        push(b.std, canopyGeo, m);
        for (let i = 0; i < 6; i++) {
          const gx = -s.w / 2 + 0.35 + (i / 5) * (s.w - 0.7);
          push(
            b.std,
            part.sphere(
              0.12,
              rng.pick(['#e8a33a', '#c94a3a', '#7fbf5a', '#e6d8b0', '#b07ad0']),
              { pos: [gx, 1.0, rng.range(-0.3, 0.3)] },
              8,
              6,
            ),
            m,
          );
        }
        break;
      }
      case 'tent': {
        const r = s.w / 2;
        const cloth = s.roofColor ?? '#7b6a4e';
        push(b.std, part.cone(r, 2.8, cloth, { pos: [0, 1.4, 0], rot: [0, Math.PI / 4, 0] }, 4), m);
        push(
          b.std,
          part.cone(
            r * 0.35,
            1.2,
            shade(cloth, -0.18),
            { pos: [0, 0.6, r * 0.62], rot: [0.32, Math.PI / 4, 0] },
            4,
          ),
          m,
        );
        push(b.std, part.cyl(0.04, 0.04, 0.6, WOOD, { pos: [0, 3.0, 0] }, 5), m);
        push(b.std, part.box(0.4, 0.25, 0.02, '#8e2f2a', { pos: [0.2, 3.1, 0] }), m);
        break;
      }
      case 'campfire': {
        for (let i = 0; i < 9; i++) {
          const a = (i / 9) * Math.PI * 2;
          push(
            b.std,
            part.dodeca(0.22, STONE_DARK, { pos: [Math.sin(a) * 0.75, 0.1, Math.cos(a) * 0.75] }),
            m,
          );
        }
        for (let i = 0; i < 3; i++)
          push(
            b.std,
            part.cyl(
              0.07,
              0.07,
              1.0,
              '#3a261a',
              { pos: [0, 0.15, 0], rot: [Math.PI / 2 - 0.3, (i * Math.PI) / 3, 0] },
              6,
            ),
            m,
          );
        campfires.push(new THREE.Vector3(s.x, 0.3, s.z));
        break;
      }
      case 'palisade': {
        const n = Math.max(3, Math.round(s.w / 0.45));
        for (let i = 0; i < n; i++) {
          const lx = -s.w / 2 + (s.w * (i + 0.5)) / n;
          const h = (s.h ?? 3.2) * (0.88 + rng.next() * 0.2);
          push(b.std, part.cyl(0.2, 0.22, h, '#5a3e2a', { pos: [lx, h / 2, 0] }, 6), m);
          push(b.std, part.cone(0.2, 0.45, '#6b4a33', { pos: [lx, h + 0.22, 0] }, 6), m);
        }
        push(b.std, part.box(s.w, 0.16, 0.12, '#3a261a', { pos: [0, 1.0, 0.22] }), m);
        break;
      }
      case 'fence': {
        const n = Math.max(1, Math.round(s.w / 2));
        for (let i = 0; i <= n; i++)
          push(b.std, part.box(0.14, 1.3, 0.14, WOOD_LIGHT, { pos: [-s.w / 2 + (s.w * i) / n, 0.65, 0] }), m);
        push(b.std, part.box(s.w, 0.09, 0.07, '#8a6a42', { pos: [0, 1.05, 0] }), m);
        push(b.std, part.box(s.w, 0.09, 0.07, '#8a6a42', { pos: [0, 0.55, 0] }), m);
        break;
      }
      case 'watchtower': {
        const H = s.h ?? 8;
        for (const sx of [-1, 1])
          for (const sz of [-1, 1])
            push(
              b.std,
              part.box(0.22, H, 0.22, WOOD, {
                pos: [(sx * s.w) / 2, H / 2, (sz * s.d) / 2],
                rot: [sz * 0.03, 0, -sx * 0.03],
              }),
              m,
            );
        push(b.std, part.box(s.w + 0.6, 0.18, s.d + 0.6, WOOD_LIGHT, { pos: [0, H - 1.6, 0] }), m);
        for (const sz of [-1, 1])
          push(b.std, part.box(s.w + 0.6, 0.6, 0.08, WOOD, { pos: [0, H - 1.2, (sz * (s.d + 0.6)) / 2] }), m);
        for (const sx of [-1, 1])
          push(b.std, part.box(0.08, 0.6, s.d + 0.6, WOOD, { pos: [(sx * (s.w + 0.6)) / 2, H - 1.2, 0] }), m);
        roofSet(b, m, s.w + 0.4, s.d + 0.4, 1.0, 0.5, 0.25, '#3a2f2a', WOOD, H - 0.3);
        for (let i = 0; i < 10; i++)
          push(b.std, part.box(0.7, 0.06, 0.06, WOOD_LIGHT, { pos: [0, 0.4 + i * 0.6, s.d / 2 + 0.25] }), m);
        lantern(b, m, s.w / 2 + 0.25, H - 2.2, s.d / 2 + 0.25);
        break;
      }
      case 'shrine': {
        push(b.std, part.cyl(1.25, 1.35, 0.25, STONE_DARK, { pos: [0, 0.125, 0] }, 10), m);
        push(b.std, part.cyl(0.95, 1.05, 0.25, STONE, { pos: [0, 0.37, 0] }, 10), m);
        push(b.std, part.box(0.8, 1.7, 0.32, '#a8a497', { pos: [0, 1.3, 0] }), m);
        push(
          b.std,
          part.cyl(0.4, 0.4, 0.32, '#a8a497', { pos: [0, 2.15, 0], rot: [Math.PI / 2, 0, 0] }, 12),
          m,
        );
        push(b.std, part.box(0.5, 1.1, 0.02, '#5c5a52', { pos: [0, 1.25, 0.17] }), m);
        const crystal = new THREE.Mesh(
          new THREE.OctahedronGeometry(0.32, 0),
          new THREE.MeshStandardMaterial({
            color: '#7fffd0',
            emissive: new THREE.Color('#3fe0a8'),
            emissiveIntensity: 1.6,
            roughness: 0.2,
            metalness: 0.1,
            transparent: true,
            opacity: 0.9,
          }),
        );
        crystal.position.set(s.x, 3.0, s.z);
        crystal.scale.set(1, 1.6, 1);
        const light = new THREE.PointLight('#5fffc0', 6, 9, 2);
        light.position.set(s.x, 3.0, s.z);
        group.add(crystal, light);
        shrineCrystals.push(crystal);
        const phase = rng.range(0, 6);
        animated.push({
          update: (t) => {
            crystal.rotation.y = t * 0.9 + phase;
            crystal.position.y = 3.0 + Math.sin(t * 1.6 + phase) * 0.15;
            light.intensity = 5 + Math.sin(t * 2.3 + phase) * 1.2;
          },
        });
        break;
      }
      case 'bridge': {
        const arch = 0.65;
        const segs = 10;
        for (let i = 0; i < segs; i++) {
          const z0 = -s.d / 2 + (s.d * i) / segs;
          const z1 = -s.d / 2 + (s.d * (i + 1)) / segs;
          const y0 = bridgeArch(z0, s.d, arch);
          const y1 = bridgeArch(z1, s.d, arch);
          const len = Math.hypot(z1 - z0, y1 - y0);
          const ang = Math.atan2(y1 - y0, z1 - z0);
          push(
            b.std,
            part.box(s.w, 0.3, len + 0.04, i % 2 ? STONE : '#99958a', {
              pos: [0, (y0 + y1) / 2 - 0.12, (z0 + z1) / 2],
              rot: [-ang, 0, 0],
            }),
            m,
          );
          for (const sx of [-1, 1]) {
            push(
              b.std,
              part.box(0.22, 0.75, len + 0.02, STONE_DARK, {
                pos: [(sx * s.w) / 2, (y0 + y1) / 2 + 0.35, (z0 + z1) / 2],
                rot: [-ang, 0, 0],
              }),
              m,
            );
          }
        }
        for (const sx of [-1, 1])
          for (const sz of [-1, 1])
            push(b.std, part.box(0.4, 1.2, 0.4, STONE, { pos: [(sx * s.w) / 2, 0.6, (sz * s.d) / 2] }), m);
        push(b.std, part.box(s.w - 0.4, 1.2, 0.8, STONE_DARK, { pos: [0, -0.45, 0] }), m);
        break;
      }
      case 'rock': {
        const g = new THREE.DodecahedronGeometry(s.w / 2, 1);
        const p = g.getAttribute('position');
        const rr = createRng(Math.round(s.x * 31 + s.z * 17));
        // Jitter by position hash so shared corners move together (no cracks between faces).
        const jitter = new Map<string, number>();
        for (let i = 0; i < p.count; i++) {
          const key = `${p.getX(i).toFixed(3)},${p.getY(i).toFixed(3)},${p.getZ(i).toFixed(3)}`;
          let k = jitter.get(key);
          if (k === undefined) {
            k = 0.82 + rr.next() * 0.3;
            jitter.set(key, k);
          }
          p.setXYZ(i, p.getX(i) * k, p.getY(i) * k * 0.7, p.getZ(i) * k);
        }
        g.computeVertexNormals();
        const rg = colorize(g, rr.pick(['#7d7b74', '#86837a', '#6f6d66']));
        rg.applyMatrix4(new THREE.Matrix4().makeTranslation(0, s.w * 0.18, 0));
        push(b.std, rg, m);
        break;
      }
      case 'stone_lantern':
        push(b.std, part.box(0.6, 0.2, 0.6, STONE_DARK, { pos: [0, 0.1, 0] }), m);
        push(b.std, part.cyl(0.12, 0.15, 0.9, STONE, { pos: [0, 0.65, 0] }, 6), m);
        push(b.std, part.box(0.55, 0.12, 0.55, STONE, { pos: [0, 1.15, 0] }), m);
        push(b.glow, part.box(0.32, 0.32, 0.32, '#ffb46a', { pos: [0, 1.38, 0] }), m);
        push(b.std, part.cone(0.48, 0.38, STONE_DARK, { pos: [0, 1.73, 0], rot: [0, Math.PI / 4, 0] }, 4), m);
        break;
      case 'anvil':
        push(b.std, part.box(0.35, 0.55, 0.35, '#3a2a1c', { pos: [0, 0.27, 0] }), m);
        push(b.std, part.box(0.9, 0.25, 0.35, '#40444a', { pos: [0, 0.68, 0] }), m);
        push(
          b.std,
          part.cone(0.12, 0.35, '#40444a', { pos: [0.6, 0.68, 0], rot: [0, 0, -Math.PI / 2] }, 6),
          m,
        );
        break;
      case 'crate_stack':
        push(b.std, part.box(0.8, 0.8, 0.8, '#8a6a42', { pos: [-0.35, 0.4, 0] }), m);
        push(b.std, part.box(0.8, 0.8, 0.8, '#7a5a38', { pos: [0.45, 0.4, 0.1], rot: [0, 0.3, 0] }), m);
        push(b.std, part.box(0.75, 0.75, 0.75, '#94744a', { pos: [0, 1.17, 0.05], rot: [0, -0.2, 0] }), m);
        break;
      case 'weapon_rack':
        for (const sx of [-1, 1]) push(b.std, part.box(0.1, 1.6, 0.1, WOOD, { pos: [sx * 1.1, 0.8, 0] }), m);
        push(b.std, part.box(2.4, 0.1, 0.12, WOOD, { pos: [0, 1.4, 0] }), m);
        for (let i = 0; i < 4; i++)
          push(
            b.std,
            part.box(0.04, 1.4, 0.1, '#b9bec4', { pos: [-0.75 + i * 0.5, 0.9, 0.08], rot: [0, 0, 0.15] }),
            m,
          );
        break;
      case 'haystack':
        push(
          b.std,
          part.sphere(s.w / 2, '#cfae5a', { pos: [0, s.w * 0.25, 0], scale: [1, 0.9, 1] }, 10, 8),
          m,
        );
        push(b.std, part.cone(s.w * 0.42, s.w * 0.6, '#d8bb6a', { pos: [0, s.w * 0.75, 0] }, 10), m);
        break;
      case 'pond':
        for (let i = 0; i < 18; i++) {
          const a = (i / 18) * Math.PI * 2;
          const r = s.w / 2 + 0.2;
          push(
            b.std,
            part.dodeca(rng.range(0.25, 0.42), rng.pick([STONE, STONE_DARK, '#9a968a']), {
              pos: [Math.sin(a) * r, 0.05, Math.cos(a) * r],
            }),
            m,
          );
        }
        for (let i = 0; i < 7; i++) {
          const a = rng.range(0, Math.PI * 2);
          const r = rng.range(0.5, s.w / 2 - 0.8);
          push(
            b.std,
            part.cyl(0.32, 0.32, 0.02, '#4f8a4a', { pos: [Math.sin(a) * r, 0.1, Math.cos(a) * r] }, 9),
            m,
          );
          if (i % 3 === 0)
            push(
              b.std,
              part.sphere(0.08, '#f2b8c8', { pos: [Math.sin(a) * r, 0.16, Math.cos(a) * r] }, 6, 5),
              m,
            );
        }
        break;
    }
  }

  const tile = roofTileTexture();
  const stdMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0 });
  const roofMat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    map: tile,
    roughness: 0.55,
    metalness: 0.1,
    side: THREE.DoubleSide,
  });
  const glowMat = new THREE.MeshBasicMaterial({
    vertexColors: true,
    color: new THREE.Color(2.2, 1.6, 1.3),
    toneMapped: true,
  });
  const addMerged = (
    list: THREE.BufferGeometry[],
    mat: THREE.Material,
    shadows: boolean,
    name: string,
  ): void => {
    if (!list.length) return;
    // Keep only attributes shared by every geometry in the bucket.
    const geo = mergeGeometries(list.map(normaliseAttrs(list)), false);
    for (const g of list) g.dispose();
    if (!geo) return;
    geo.computeBoundingSphere();
    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = name;
    mesh.castShadow = shadows;
    mesh.receiveShadow = shadows;
    group.add(mesh);
  };
  addMerged(b.std, stdMat, true, 'structures-std');
  addMerged(b.roof, roofMat, true, 'structures-roof');
  addMerged(b.glow, glowMat, false, 'structures-glow');
  for (const p of plaques) group.add(p);
  return { group, animated, shrineCrystals, campfires };
}

function normaliseAttrs(list: THREE.BufferGeometry[]): (g: THREE.BufferGeometry) => THREE.BufferGeometry {
  const hasUv = list.every((g) => g.getAttribute('uv'));
  return (g) => {
    let geo = g.index ? g.toNonIndexed() : g;
    for (const name of Object.keys(geo.attributes)) {
      if (name === 'position' || name === 'normal' || name === 'color' || (name === 'uv' && hasUv)) continue;
      geo.deleteAttribute(name);
    }
    if (!hasUv) geo.deleteAttribute('uv');
    if (geo === g) geo = g;
    return geo;
  };
}

function colorize(g: THREE.BufferGeometry, color: string): THREE.BufferGeometry {
  const geo = g.index ? g.toNonIndexed() : g;
  geo.deleteAttribute('uv');
  const c = new THREE.Color(color);
  const n = geo.getAttribute('position').count;
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    arr[i * 3] = c.r;
    arr[i * 3 + 1] = c.g;
    arr[i * 3 + 2] = c.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  if (!geo.getAttribute('normal')) geo.computeVertexNormals();
  return geo;
}

/** Height of the arched bridge deck at local z (for visuals only; gameplay is flat). */
export function bridgeArch(z: number, d: number, arch: number): number {
  const u = (2 * z) / d;
  return Math.max(0, arch * (1 - u * u)) + 0.15;
}
